import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import { 
  ArrowRight, CheckCircle, TrendingUp, Shield, Target, Bell, 
  ChevronRight, Activity, LineChart, BarChart3, Zap, Layers
} from 'lucide-react';

const faqData = [
  {
    question: "ETH sinyalleri nedir?",
    answer: "ETH sinyalleri, Ethereum fiyat hareketlerine dayalı teknik analiz sonuçlarıdır. Borsamex'in Sniper Engine'i ETH/USDT paritesini 7/24 analiz ederek potansiyel giriş noktalarını, TP ve SL seviyelerini belirler."
  },
  {
    question: "Ethereum sinyalleri ne kadar güvenilir?",
    answer: "Hiçbir sinyal sistemi %100 doğruluk garantisi veremez. Ethereum yüksek volatiliteye sahip bir varlıktır. Borsamex şeffaf performans verileri sunar ve risk yönetiminin önemini vurgular."
  },
  {
    question: "ETH sinyal sistemi nasıl çalışır?",
    answer: "Sistem ETH/USDT paritesini sürekli tarar, RSI, MACD, Bollinger gibi teknik göstergelerle analiz eder, Sniper Engine skorlama yapar ve kaliteli setupları sinyal olarak yayınlar."
  },
  {
    question: "Ethereum için LONG ve SHORT sinyal var mı?",
    answer: "Evet. Borsamex hem LONG (yukarı yönlü) hem SHORT (aşağı yönlü) ETH sinyalleri üretir. Piyasa koşullarına göre her iki yönde de fırsat bildirimleri alabilirsiniz."
  },
  {
    question: "ETH sinyalleri BTC sinyallerinden farklı mı?",
    answer: "Evet. ETH ve BTC farklı volatilite ve likidite profillerine sahiptir. Ethereum ekosistemi (DeFi, NFT, Layer 2) haberlere daha duyarlıdır. Sistem bu özellikleri dikkate alarak analiz yapar."
  }
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqData.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": { "@type": "Answer", "text": item.answer }
  }))
};

const EthSinyalleriPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="ETH Sinyalleri – Ethereum Al-Sat Sinyalleri | Borsamex"
        description="ETH sinyalleri ile Ethereum fırsatlarını yakalayın. Sniper Engine 7/24 ETH/USDT paritesini analiz eder, TP/SL seviyeleri ile risk odaklı sinyaller üretir."
        keywords="eth sinyalleri, ethereum sinyalleri, eth trading sinyalleri, ethereum sinyal, eth usdt sinyal, ethereum teknik analiz, eth al sat"
        canonical="https://borsamex.com/eth-sinyalleri"
        structuredData={structuredData}
      />

      {/* HERO */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-transparent to-brand-green/5" />
        <div className="container mx-auto px-4 max-w-5xl relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <Badge className="mb-6 px-4 py-2 bg-blue-500/10 text-blue-400 border-blue-500/20">
              <Layers className="h-4 w-4 mr-2" />
              Altcoin Lideri
            </Badge>
            
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight">
              ETH Sinyalleri<br />
              <span className="text-blue-400">Ethereum Al-Sat Fırsatları</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              Borsamex'in <strong className="text-white">Sniper Engine</strong> sistemi, 
              ETH/USDT paritesini <strong className="text-blue-400">7/24</strong> analiz ederek 
              potansiyel giriş noktalarını ve risk seviyelerini belirler.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-8">
                  ETH Sinyallerini Gör <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/performans">
                <Button size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  <BarChart3 className="mr-2 h-5 w-5" />
                  Performans
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ETH Analizi */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ethereum Sinyalleri Nasıl Üretilir?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Card className="border-blue-500/30 bg-gradient-to-br from-blue-900/20 to-slate-900/80">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <span className="text-2xl">Ξ</span>
                    </div>
                    <div>
                      <span className="font-bold text-white text-xl">ETH-USDT</span>
                      <Badge className="ml-2 bg-green-500/20 text-green-400 border-green-500/30 text-xs">AKTİF</Badge>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <span className="text-slate-400 text-sm">Tarama Sıklığı</span>
                      <span className="font-medium text-brand-green">7/24</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <span className="text-slate-400 text-sm">Sinyal Türü</span>
                      <span className="font-medium text-white">LONG & SHORT</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <span className="text-slate-400 text-sm">TP Seviyeleri</span>
                      <span className="font-medium text-green-400">TP1 / TP2 / TP3</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <span className="text-slate-400 text-sm">Risk Yönetimi</span>
                      <span className="font-medium text-red-400">ATR Bazlı SL</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                  <Activity className="h-5 w-5 text-blue-400" />
                  Sürekli İzleme
                </h3>
                <p className="text-slate-400">
                  ETH/USDT paritesi kesintisiz olarak izlenir. Her 15 dakikada derinlemesine teknik analiz yapılır. 
                  Ethereum'un dinamik yapısına uygun hızlı tepki verilir.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                  <LineChart className="h-5 w-5 text-purple-400" />
                  Teknik Göstergeler
                </h3>
                <p className="text-slate-400">
                  RSI, MACD, Bollinger Bands, ATR ve 200+ gösterge ile analiz. Ethereum'un BTC'den bağımsız 
                  hareketleri de dikkate alınır.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                  <Layers className="h-5 w-5 text-cyan-400" />
                  Ekosistem Farkındalığı
                </h3>
                <p className="text-slate-400">
                  DeFi, NFT ve Layer 2 gelişmeleri Ethereum fiyatını etkiler. Sistem bu dinamikleri 
                  volatilite hesaplamalarına yansıtır.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Demo ETH Sinyal Kartı */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Demo Card */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <Card className="border-blue-500/30 bg-gradient-to-br from-slate-900 to-slate-800 overflow-hidden">
                <CardContent className="p-0">
                  <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <span className="text-lg">Ξ</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-white">ETH-USDT</span>
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">LONG</Badge>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <span>Güç:</span>
                          {[1,2,3,4].map(i => <span key={i} className="text-blue-400">★</span>)}
                          <span className="text-slate-600">★</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 space-y-3">
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">GİRİŞ</p>
                      <p className="font-mono text-xl text-white font-bold">$3,420</p>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2">
                      <div className="p-2 bg-green-900/20 rounded border border-green-500/20 text-center">
                        <span className="text-xs text-green-400 block">TP1</span>
                        <span className="font-mono text-green-400 text-sm">$3,520</span>
                      </div>
                      <div className="p-2 bg-emerald-900/20 rounded border border-emerald-500/20 text-center">
                        <span className="text-xs text-emerald-400 block">TP2</span>
                        <span className="font-mono text-emerald-400 text-sm">$3,680</span>
                      </div>
                      <div className="p-2 bg-red-900/20 rounded border border-red-500/20 text-center">
                        <span className="text-xs text-red-400 block">STOP</span>
                        <span className="font-mono text-red-400 text-sm">$3,280</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="px-4 py-2 bg-slate-800/30 border-t border-slate-700/50">
                    <p className="text-xs text-slate-500 text-center">Örnek ETH sinyal kartı</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Yön Türleri - Internal Links */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-4">
              <h3 className="text-xl font-bold text-white mb-4">ETH Sinyal Yönleri</h3>
              
              <Link to="/long-sinyaller" className="block group">
                <div className="p-4 bg-green-900/10 border border-green-500/20 rounded-lg hover:border-green-500/40 transition-colors">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="h-6 w-6 text-green-400" />
                    <div className="flex-1">
                      <h4 className="font-bold text-white group-hover:text-green-400 transition-colors">LONG Pozisyonlar</h4>
                      <p className="text-sm text-slate-400">Yükseliş beklentisi ile alım fırsatları</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-500 group-hover:text-green-400 group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              </Link>
              
              <Link to="/short-sinyaller" className="block group">
                <div className="p-4 bg-red-900/10 border border-red-500/20 rounded-lg hover:border-red-500/40 transition-colors">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="h-6 w-6 text-red-400 rotate-180" />
                    <div className="flex-1">
                      <h4 className="font-bold text-white group-hover:text-red-400 transition-colors">SHORT Pozisyonlar</h4>
                      <p className="text-sm text-slate-400">Düşüş beklentisi ile satış fırsatları</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-500 group-hover:text-red-400 group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              </Link>
              
              <Link to="/btc-sinyalleri" className="block group">
                <div className="p-4 bg-amber-900/10 border border-amber-500/20 rounded-lg hover:border-amber-500/40 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="text-xl text-amber-400">₿</span>
                    <div className="flex-1">
                      <h4 className="font-bold text-white group-hover:text-amber-400 transition-colors">BTC Sinyalleri</h4>
                      <p className="text-sm text-slate-400">Bitcoin özelinde tüm sinyaller</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-500 group-hover:text-amber-400 group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              </Link>
              
              <Link to="/ai-kripto-sinyalleri" className="block group">
                <div className="p-4 bg-purple-900/10 border border-purple-500/20 rounded-lg hover:border-purple-500/40 transition-colors">
                  <div className="flex items-center gap-3">
                    <Zap className="h-6 w-6 text-purple-400" />
                    <div className="flex-1">
                      <h4 className="font-bold text-white group-hover:text-purple-400 transition-colors">Tüm AI Sinyalleri</h4>
                      <p className="text-sm text-slate-400">ETH dahil tüm paritelerde sinyal sistemi</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-500 group-hover:text-purple-400 group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Özellikler */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <TrendingUp className="h-7 w-7" />, title: "LONG Sinyaller", desc: "Yukarı yönlü fırsatlar", color: "bg-green-500/20 text-green-400" },
              { icon: <TrendingUp className="h-7 w-7 rotate-180" />, title: "SHORT Sinyaller", desc: "Aşağı yönlü fırsatlar", color: "bg-red-500/20 text-red-400" },
              { icon: <Bell className="h-7 w-7" />, title: "Anlık Bildirim", desc: "Telegram + Web", color: "bg-purple-500/20 text-purple-400" },
              { icon: <Shield className="h-7 w-7" />, title: "Risk Kontrolü", desc: "ATR bazlı SL", color: "bg-blue-500/20 text-blue-400" }
            ].map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }} viewport={{ once: true }}>
                <Card className="border-slate-700/50 bg-slate-900/50 h-full text-center">
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-2xl ${item.color} flex items-center justify-center mx-auto mb-4`}>{item.icon}</div>
                    <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ETH vs BTC Karşılaştırma */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">ETH vs BTC: Sinyal Farkları</h2>
            <p className="text-slate-400">Her iki varlık için optimize edilmiş analiz parametreleri</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-blue-500/30 bg-slate-900/50">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <span className="text-lg">Ξ</span>
                  </div>
                  <h3 className="font-bold text-white text-lg">Ethereum (ETH)</h3>
                </div>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-blue-400" /> Daha yüksek volatilite</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-blue-400" /> DeFi/NFT haber duyarlılığı</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-blue-400" /> Altcoin korelasyonu yüksek</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-blue-400" /> Daha geniş TP aralıkları</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-amber-500/30 bg-slate-900/50">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <span className="text-lg">₿</span>
                  </div>
                  <h3 className="font-bold text-white text-lg">Bitcoin (BTC)</h3>
                </div>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-amber-400" /> Daha stabil trend yapısı</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-amber-400" /> Makro ekonomi duyarlılığı</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-amber-400" /> Piyasa lideri korelasyonu</li>
                  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-amber-400" /> Daha dar SL seviyeleri</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Rehber Bölümü */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Yeni Misin? Temel Kavramları Öğren</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/rehber/kripto-sinyali-nedir" className="group">
              <Card className="border-purple-500/20 bg-slate-900/50 h-full hover:border-purple-500/50 transition-colors">
                <CardContent className="p-5">
                  <h3 className="font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">Kripto Sinyali Nedir?</h3>
                  <p className="text-slate-400 text-sm">TP, Stop Loss ve temel kavramlar</p>
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/rehber/tp-ve-stop-loss-nedir" className="group">
              <Card className="border-green-500/20 bg-slate-900/50 h-full hover:border-green-500/50 transition-colors">
                <CardContent className="p-5">
                  <h3 className="font-bold text-white mb-2 group-hover:text-green-400 transition-colors">TP ve Stop Loss Nedir?</h3>
                  <p className="text-slate-400 text-sm">Hedef ve risk seviyeleri</p>
                </CardContent>
              </Card>
            </Link>
            
            <Link to="/rehber/yeni-baslayanlar-icin-risk-yonetimi" className="group">
              <Card className="border-red-500/20 bg-slate-900/50 h-full hover:border-red-500/50 transition-colors">
                <CardContent className="p-5">
                  <h3 className="font-bold text-white mb-2 group-hover:text-red-400 transition-colors">Risk Yönetimi</h3>
                  <p className="text-slate-400 text-sm">Sermaye koruma stratejileri</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-slate-900/50">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">ETH Sinyalleri Hakkında</h2>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }} viewport={{ once: true }}>
                <Card className="border-slate-700/50 bg-slate-900/30">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-white mb-2">{faq.question}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ethereum Sinyallerini Takip Et
            </h2>
            <p className="text-slate-400 mb-8">
              Ücretsiz kaydol ve ETH/USDT sinyallerini anında almaya başla.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-10">
                Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            
            <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-brand-green" />
                <span>Kredi kartı gerekmez</span>
              </div>
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-brand-green" />
                <span>Anlık Telegram bildirimleri</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EthSinyalleriPage;
